import type { PropsWithChildren } from 'react';

import { cn } from '../../../../../utils';
import type { ButtonProps } from '../../../Button';
import { Button } from '../../../Button';
import Spinner from '../../../Loader/Loader';
import { Typography } from '../../../Typography';
import styles from './DialogLayout.module.scss';

export const DialogButton = ({
  isLoading,
  disabled,
  children,
  ...props
}: ButtonProps & { isLoading?: boolean }) => {
  return (
    <div className={styles['dialog-button']}>
      <Button {...props} disabled={isLoading || disabled}>
        {isLoading ? <Spinner size="sm" color="light" /> : children}
      </Button>
    </div>
  );
};

interface DialogContainerProps {
  title?: string;
  onCancel?: () => void;
  onSubmit?: () => void;
  isSubmitPending?: boolean;
  cancelButtonText?: string;
  submitButtonText?: string;
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideSubmitButton?: boolean;
  hideCancelButton?: boolean;
  className?: string;
}

const DialogLayout = ({
  children,
  title,
  onCancel,
  onSubmit,
  cancelButtonText = 'Cancel',
  submitButtonText = 'Submit',
  hideHeader = false,
  hideFooter = false,
  hideSubmitButton = false,
  hideCancelButton = false,
  isSubmitPending,
  className,
}: PropsWithChildren<DialogContainerProps>) => {
  return (
    <div className={cn(styles['container'], className)}>
      {!hideHeader && (
        <div className={cn(styles['container__header'], styles['border-bottom'])}>
          <Typography variant="h3">{title}</Typography>
        </div>
      )}

      <div className={cn(styles['container__body'])}>{children}</div>

      {!hideFooter && (
        <div className={cn(styles['container__footer'], styles['border-top'])}>
          {!hideCancelButton && (
            <DialogButton variant="secondary" onClick={onCancel}>
              {cancelButtonText}
            </DialogButton>
          )}
          {!hideSubmitButton && (
            <DialogButton onClick={onSubmit} isLoading={isSubmitPending}>
              {submitButtonText}
            </DialogButton>
          )}
        </div>
      )}
    </div>
  );
};

export { DialogLayout };
